import { jwtDecode } from 'jwt-decode'

import { t } from '@/i18n'
import { login as authLogin, refreshToken as serviceRefreshToken } from '@/services/authService'
import { notificationService } from '@/services/notificationService'
import { setSentryUser } from '@/services/telemetry/sentryClient'
import { useDashboardStore } from '@/stores/dashboard'
import { handleApiError } from '@/utils/apiHelpers'
import { clearAuthToken, setAuthToken } from '@/utils/initializeApi'

function getWorkspaceIdFromStorage() {
    const rawWorkspace = localStorage.getItem('workspace')

    if (!rawWorkspace) {
        return undefined
    }

    try {
        const workspace = JSON.parse(rawWorkspace)
        return workspace?.workspaceId
    } catch {
        return undefined
    }
}

export function createAuthActions(state) {
    const { token, userId, userName, userEmail, user, permissionsReceived, permissions } = state
    let refreshRequest = null

    function syncSentryUserFromState() {
        const workspaceId = getWorkspaceIdFromStorage()

        if (!token.value || (!userId.value && !userName.value && !userEmail.value)) {
            setSentryUser(null)
            return
        }

        const sentryUser = {
            id: userId.value ?? undefined,
            username: userName.value ?? undefined,
            email: userEmail.value ?? undefined
        }

        if (workspaceId !== undefined) {
            sentryUser.data = { workspaceId }
        }

        setSentryUser(sentryUser)
    }

    function loginSuccess(payload) {
        token.value = payload.token
        userId.value = payload.userId
        userName.value = payload.userName
        userEmail.value = payload.userEmail

        // Associate Sentry events with the authenticated user
        syncSentryUserFromState()
    }

    function refreshTokenSuccess(newToken) {
        token.value = newToken
    }

    function logoutSuccess() {
        token.value = null
        userId.value = null
        userName.value = null
        userEmail.value = null
        user.value = null
        permissionsReceived.value = false
        permissions.value = []

        setSentryUser(null)
    }

    function login(credentials) {
        if (!permissionsReceived.value) {
            logout()
        }

        return authLogin(credentials)
            .then(({ token: tokenValue, userId: userIdValue, userName: userNameValue, userEmail: userEmailValue, response }) => {
                loginSuccess({
                    token: tokenValue,
                    userId: userIdValue,
                    userName: userNameValue,
                    userEmail: userEmailValue
                })

                localStorage.setItem('token', tokenValue)
                localStorage.setItem('userId', userIdValue)
                localStorage.setItem('userName', userNameValue)
                localStorage.setItem('userEmail', userEmailValue)

                setAuthToken(tokenValue)

                return Promise.resolve(response)
            })
            .catch((error) => {
                if (typeof error.response === 'undefined' || error.response.status !== 400) {
                    notificationService.error(t('common.auth.connectionError'))
                } else {
                    handleApiError(error)
                }
                return Promise.reject(error)
            })
    }

    function logout() {
        logoutSuccess()
        localStorage.removeItem('token')
        localStorage.removeItem('userId')
        localStorage.removeItem('userName')
        localStorage.removeItem('userEmail')
        localStorage.removeItem('receivedPermissions')
        localStorage.removeItem('userPermissions')
        clearAuthToken()

        useDashboardStore().clearDashboardState()
    }

    function refreshToken() {
        const tokenToRefresh = token.value

        if (refreshRequest?.token === tokenToRefresh) {
            return refreshRequest.promise
        }

        const promise = serviceRefreshToken(tokenToRefresh)
            .then(({ token: newToken, response }) => {
                if (token.value !== tokenToRefresh || !permissionsReceived.value) {
                    return Promise.resolve(response)
                }

                localStorage.setItem('token', newToken)
                refreshTokenSuccess(newToken)
                setAuthToken(newToken)
                return Promise.resolve(response)
            })
            .catch((error) => {
                return Promise.reject(error)
            })
            .finally(() => {
                if (refreshRequest?.promise === promise) {
                    refreshRequest = null
                }
            })

        refreshRequest = {
            token: tokenToRefresh,
            promise
        }

        return promise
    }

    async function inspectToken() {
        const TOKEN_REFRESH_EXPIRATION_MS = 1000 * 60 * 60 * 24 // 24 hours
        const TOKEN_CHECKING_PERIOD_MS = 1000 * 60 * 60 // 1 hour

        if (!token.value || !permissionsReceived.value) return

        const inspectedToken = token.value
        const decoded = jwtDecode(inspectedToken) || {}
        const expMs = decoded?.exp ? decoded.exp * 1000 : null
        const origIatSec = decoded?.orig_iat ?? null
        const nowMs = Date.now()

        // If a token is expired, log out the user
        if (expMs && nowMs >= expMs) {
            notificationService.error(t('common.auth.sessionExpired'))
            await logout()
            return
        }

        // If a token lifetime exceeds 24 hours, log out the user
        if (origIatSec && nowMs - origIatSec * 1000 >= TOKEN_REFRESH_EXPIRATION_MS) {
            notificationService.warning(t('common.auth.sessionDurationExceeded'))
            await logout()
            return
        }

        // Refresh the token if it's about to expire and within the refresh window
        if (
            expMs &&
            origIatSec &&
            expMs - nowMs < TOKEN_CHECKING_PERIOD_MS &&
            nowMs - origIatSec * 1000 < TOKEN_REFRESH_EXPIRATION_MS - TOKEN_CHECKING_PERIOD_MS
        ) {
            try {
                await refreshToken()
            } catch {
                if (token.value !== inspectedToken || !permissionsReceived.value) {
                    return
                }

                notificationService.error(t('common.auth.tokenRefreshFailed'))
                // If refresh fails, log out the user
                await logout()
            }
        }
    }

    // Restore Sentry user context on app refresh from persisted auth state.
    syncSentryUserFromState()

    return {
        login,
        logout,
        refreshToken,
        inspectToken
    }
}
